/**
 * The agent's question with tappable answers (S-525, Paper "Chat cards ·
 * spike (S-525)", C1–C5c): open, a lettered row per answer that anyone in the
 * crew can tap, then "Something else…", which opens a box in the card for
 * their own words; answered, the question and the answer (their own words in
 * quotes), with who and when.
 */
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { ArrowUp, Check, Pencil } from "lucide-react";
import { useState, type FormEvent, type KeyboardEvent } from "react";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { useAnswerChoice } from "./api";
import { choiceView, letterOf } from "./choice-view";
import { shortTime } from "./format";

const SHELL = "flex w-full max-w-[520px] flex-col self-start rounded-tile bg-card shadow-ring";
const ROW = "flex min-h-12 items-center gap-3 border-t border-border px-3.5 py-2.5 text-left";

export function ChoiceCard({
  messageId,
  threadId,
  data,
  me,
}: {
  messageId: string;
  threadId: string;
  data: Record<string, unknown>;
  me: Me;
}) {
  const view = choiceView(data, me.user.id);
  const answer = useAnswerChoice(threadId);
  const [writing, setWriting] = useState(false);
  if (view.state === "answered") {
    return (
      <div data-choice="answered" className={cn(SHELL, "gap-1.5 px-3.5 py-3")}>
        <p className="text-meta text-muted-foreground">{view.question}</p>
        <p className="flex items-start gap-2.5">
          {view.answer ? (
            <Check
              aria-hidden
              className="mt-0.5 size-4 shrink-0 rounded-full bg-moss p-0.5 text-white"
              strokeWidth={3}
            />
          ) : null}
          <span
            className={cn(
              "min-w-0 flex-1",
              view.answer
                ? "text-body font-medium text-foreground"
                : "text-sm text-muted-foreground",
            )}
          >
            {view.answer ?? "Answered in the thread"}
          </span>
          <span className="shrink-0 text-xs leading-5 text-hint">
            {view.by} · {shortTime(view.at, me.workspace.timezone)}
          </span>
        </p>
      </div>
    );
  }
  const vars = answer.isPending ? answer.variables : undefined;
  const picking = vars && "option" in vars ? vars.option : undefined;
  return (
    <div data-choice="open" className={SHELL}>
      <p className="px-3.5 pt-3.5 pb-2.5 text-base leading-[22px] font-medium text-foreground">
        {view.question}
      </p>
      <div role="group" aria-label={view.question} className="flex flex-col">
        {view.options.map((option, i) => (
          <button
            key={option}
            type="button"
            disabled={answer.isPending}
            onClick={() => answer.mutate({ messageId, option: i })}
            className={cn(
              ROW,
              "transition-colors outline-none hover:bg-accent focus-visible:bg-accent",
              "disabled:cursor-default disabled:hover:bg-transparent",
            )}
          >
            <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted font-mono text-xs font-medium text-muted-foreground">
              {picking === i ? <Spinner className="size-3" /> : letterOf(i)}
            </span>
            <span className="min-w-0 flex-1 text-body text-foreground">{option}</span>
          </button>
        ))}
        {writing ? (
          <OwnAnswer
            pending={answer.isPending}
            onSend={(text) => answer.mutate({ messageId, text })}
            onCancel={() => setWriting(false)}
          />
        ) : (
          <button
            type="button"
            disabled={answer.isPending}
            onClick={() => setWriting(true)}
            className={cn(
              ROW,
              "transition-colors outline-none hover:bg-accent focus-visible:bg-accent",
              "disabled:cursor-default disabled:hover:bg-transparent",
            )}
          >
            <span className="flex size-6 shrink-0 items-center justify-center rounded-md shadow-[inset_0_0_0_1px_var(--color-input)]">
              <Pencil aria-hidden className="size-3.5 text-muted-foreground" />
            </span>
            <span className="min-w-0 flex-1 text-body text-muted-foreground">Something else…</span>
          </button>
        )}
      </div>
      <p className="border-t border-border px-3.5 pt-2.5 pb-3 text-xs text-muted-foreground">
        {answer.error ? (
          <span className="text-brick">{errorHint(answer.error)}</span>
        ) : (
          "Tap one, or reply in the thread."
        )}
      </p>
    </div>
  );
}

/**
 * "Something else…" open (Paper C5b): a box in the card for their own words,
 * sent with its arrow or Enter (Shift+Enter for a new line); Escape, or
 * leaving it empty, closes it.
 */
function OwnAnswer({
  pending,
  onSend,
  onCancel,
}: {
  pending: boolean;
  onSend: (text: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState("");
  const send = (e?: FormEvent) => {
    e?.preventDefault();
    if (text.trim() && !pending) onSend(text.trim());
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) send(e);
    if (e.key === "Escape") onCancel();
  };
  return (
    <form
      onSubmit={send}
      className="flex items-center gap-3 border-t border-border bg-accent px-3.5 pt-2.5 pb-3"
    >
      <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-foreground">
        <Pencil aria-hidden className="size-3.5 text-card" />
      </span>
      <div className="flex min-w-0 flex-1 items-end gap-2 rounded-md bg-card py-1.5 pr-1.5 pl-2.5 shadow-[inset_0_0_0_1px_var(--color-foreground)]">
        <textarea
          autoFocus
          rows={1}
          value={text}
          disabled={pending}
          aria-label="Your answer"
          placeholder="Your answer"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          onBlur={() => {
            if (!text.trim()) onCancel();
          }}
          className="field-sizing-content max-h-40 min-w-0 flex-1 resize-none bg-transparent py-0.5 text-body text-foreground outline-none placeholder:text-hint"
        />
        <button
          type="submit"
          aria-label="Send"
          disabled={pending || !text.trim()}
          className="flex size-7 shrink-0 items-center justify-center rounded-md bg-foreground text-primary-foreground disabled:opacity-40"
        >
          {pending ? <Spinner className="size-3" /> : <ArrowUp aria-hidden className="size-3.5" />}
        </button>
      </div>
    </form>
  );
}
